import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { preventInputNumberOnWheel } from '../../../helpers/utils';
import { IconType } from 'react-icons/lib';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  id?: string;
  dataTestId?: string;
  placeholder?: string;
  style?: React.CSSProperties;
  iconPosition?: 'left' | 'right';
  iconClassName?: string;
  label?: string | ReactNode;
  labelClassName?: string;
  register?: { [name: string]: string | number | boolean | object };
  danger?: string;
  showMessageError?: boolean;
  required?: boolean;
  prefixComponent?: React.ReactNode;
  focus?: string;
  size?: 'small' | 'medium' | 'large';
  type?: string;
  disabled?: boolean;
  error?: string;
  icon?: React.ReactNode | IconType;
  containerClassName?: string;
  formatText?: string;
  maxLength?: number;
  inputClassName?: string;
  inputBackground?: string;
  handleOnClickIcon?: (e: React.FormEvent) => void;
  inputWrapperClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  isCampaignInquiry?: boolean;
  errorClassName?: string;
  isUseCurrencyFormat?: boolean;
  isUsePercentageFormat?: boolean;
  maxDigit?: number;
  decimal?: number;
  isDefaultNullCurrency?: boolean;
  currencyPrefix?: string;
  infoField?: string;
}

const Input = ({
  id,
  dataTestId,
  placeholder,
  style,
  iconPosition,
  iconClassName,
  label,
  labelClassName,
  register,
  danger,
  showMessageError = true,
  required,
  prefixComponent = <Fragment />,
  focus = 'outline-none',
  type = 'text',
  disabled = false,
  error = '',
  icon = false,
  containerClassName = '',
  formatText = null,
  maxLength = null,
  inputClassName = '',
  inputBackground = 'bg-reliableBlack3',
  handleOnClickIcon = () => {},
  inputWrapperClassName = '',
  size = 'large',
  inputRef,
  isCampaignInquiry = false,
  errorClassName = '',
  isUseCurrencyFormat = false,
  isUsePercentageFormat = false,
  decimal = 3,
  isDefaultNullCurrency = true,
  currencyPrefix = 'Rp',
  infoField,
  ...props
}: InputProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [isInputFocus, setIsInputFocus] = useState(false);
  const [isAlreadySetDefaultValue, setIsAlreadySetDefaultValue] = useState(false);

  const sizeClassName = {
    small: `w-full px-4 py-2 lg:px-5 lg:py-4 ${!isCampaignInquiry && 'max-h-[30px] lg:max-h-[40px]'} text-sm lg:text-sm leading-4`,
    medium: `w-full px-4 py-2 lg:px-5 lg:py-4 ${!isCampaignInquiry && 'max-h-[30px] lg:max-h-[40px]'} text-base lg:text-base leading-4`,
    large: `w-full px-4 py-2 lg:px-5 lg:py-4 ${!isCampaignInquiry && 'max-h-[30px] lg:max-h-[40px]'} text-base lg:text-base leading-4`,
  };

  // tailwind styling
  const dafaultStyling = `w-full outline-none ${inputBackground} bg-reliableBlack3 text-reliableBlack border-b border-reliableBlack30 placeholder:text-reliableBlack60 focus:border-reliableBlack60 ${
    disabled && 'disabled:bg-reliableBlack10 disabled:text-reliableBlack50'
  }`;

  const handleKeyDown = (e) => {
    // Check if the input type is number and the key pressed is an arrow key
    if (type === 'number') {
      // Prevent letters
      const charCode = e.keyCode;
      if (charCode > 64 && charCode < 91) {
        e.preventDefault();
        return;
      }

      // Prevent special chars
      if (['ArrowUp', 'ArrowDown', '.', ',', '+', '-'].includes(e.key)) {
        e.preventDefault();
        return;
      }
    }
  };

  // currency format
  const [value, setValue] = useState('');

  const formatCurrency = (value: string) => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '');

    if (numbers === '') return '';

    const number = numbers.length > 1 && numbers.startsWith('0') ? numbers.slice(1) : numbers;

    // Add thousand separators
    const formatted = number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${currencyPrefix}${formatted}`;
  };

  const handleChangeCurrency = (e) => {
    const inputValue = e.target.value;
    // Remove "Rp " prefix and dots to get raw numbers
    const numbers = props.maxDigit
      ? inputValue.replace(/[^\d]/g, '').slice(0, props.maxDigit)
      : inputValue.replace(/[^\d]/g, '');
    if (numbers && !isNaN(numbers)) {
      setValue(formatCurrency(numbers));
      e.target.value = numbers;
    } else if (!isDefaultNullCurrency) {
      setValue(`${currencyPrefix}0`);
      e.target.value = '0';
    } else {
      setValue('');
      e.target.value = '';
    }
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleFocus = (e) => {
    // Move cursor to end when focused
    setIsInputFocus(true);
    const input = e.target;
    setTimeout(() => {
      input.setSelectionRange(input.value.length, input.value.length);
    }, 0);
  };

  // Percentage format
  const [cursorPosition, setCursorPosition] = useState(0);

  const formatPercentage = (input) => {
    let numbers = input.replace(/[^\d.]/g, '');

    if (numbers === '') return '';

    const parts = numbers.split('.');
    if (parts.length > 2) {
      numbers = parts[0] + '.' + parts.slice(1).join('');
    }

    let numValue = parseFloat(numbers);
    if (isNaN(numValue)) return '';

    if (numValue > 100) {
      numValue = 100;
    }

    return (Math.floor(numValue * Math.pow(10, decimal)) / Math.pow(10, decimal)).toFixed(decimal);
  };

  const [shouldUpdateCursor, setShouldUpdateCursor] = useState(false);

  const updateCursorPointer = (position: number) => {
    setCursorPosition(position);
    setShouldUpdateCursor(true);
  };

  const handleOnChange = (e, value: string) => {
    e.target.value = value;
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleChangePercentage = (e) => {
    const inputValue = e.target.value;
    const currentCursorPos = e.target.selectionStart;
    const previousValue = value;

    // Detect if this is a deletion by comparing lengths
    const isDeletion = inputValue.length < previousValue.length;

    if (inputValue === '') {
      setValue('');
      updateCursorPointer(0);
      handleOnChange(e, '');
      return;
    }

    // Handle case where input starts with decimal point (e.g., ".5")
    if (inputValue.startsWith('.')) {
      const newValue = '0' + inputValue;
      setValue(newValue);
      updateCursorPointer(currentCursorPos + 1);
      handleOnChange(e, newValue);
      return;
    }

    const previewBeforeDecimal = previousValue.split('.')[0];
    const inputValueBeforeDecimal = inputValue.split('.')[0];
    const numValue = parseFloat(inputValue);

    if (
      previousValue.includes('.') &&
      inputValue.includes('.') &&
      previewBeforeDecimal === '0' &&
      inputValueBeforeDecimal.length === 2 &&
      inputValueBeforeDecimal.startsWith('0') &&
      !isNaN(numValue) &&
      !isNaN(Number(inputValueBeforeDecimal))
    ) {
      setValue(Number(inputValueBeforeDecimal) + '.' + inputValue.split('.')[1]);
      updateCursorPointer(1);
      handleOnChange(e, Number(inputValueBeforeDecimal) + '.' + inputValue.split('.')[1]);
      return;
    }

    // Check if decimal point was attempted to be deleted
    if (isDeletion && previousValue.includes('.') && !inputValue.includes('.')) {
      // User tried to delete the decimal point, but we want to prevent this
      // Move cursor to right before the decimal point
      const decimalIndex = previousValue.indexOf('.');
      setValue(previousValue);
      updateCursorPointer(decimalIndex);
      handleOnChange(e, previousValue);
      return;
    }

    if (!/^[\d]*\.?[\d]*$/.test(inputValue)) {
      if (inputValue.includes('..')) {
        const newValue = inputValue.replace(/\.\./g, '.');
        setValue(newValue);
        updateCursorPointer(newValue.indexOf('.') + 1);
        handleOnChange(e, newValue);
        return;
      } else {
        // Invalid input - reject it
        setValue(previousValue);
        updateCursorPointer(currentCursorPos - 1);
        handleOnChange(e, previousValue);
        return;
      }
    }

    if (!isNaN(numValue)) {
      if (numValue > 100) {
        // Value over 100 - reject it
        setValue(previousValue);
        updateCursorPointer(currentCursorPos - 1);
        handleOnChange(e, previousValue);
        return;
      }

      const formatted = formatPercentage(inputValue);
      setValue(formatted);

      let newCursorPos = currentCursorPos;

      if (isDeletion) {
        // For deletions, keep the cursor where it is
        newCursorPos = currentCursorPos;
      } else {
        // For additions, handle cursor positioning
        const decimalIndex = formatted.indexOf('.');

        if (inputValue.includes('.')) {
          const inputDecimalIndex = inputValue.indexOf('.');
          if (currentCursorPos > inputDecimalIndex) {
            newCursorPos = Math.min(currentCursorPos, formatted.length);
          } else {
            newCursorPos = currentCursorPos;
          }
        } else {
          newCursorPos = Math.min(
            currentCursorPos,
            decimalIndex > -1 ? decimalIndex : formatted.length
          );
        }
      }

      updateCursorPointer(newCursorPos);
      handleOnChange(e, formatted);
    } else {
      // Check if it's just a decimal point
      if (inputValue === '.') {
        const newValue = '0.';
        setValue(newValue);
        updateCursorPointer(2);
        handleOnChange(e, newValue);
        return;
      }

      // Invalid number - reject it
      setValue(previousValue);
      updateCursorPointer(currentCursorPos - 1);
      handleOnChange(e, previousValue);
    }
  };

  // Set cursor position only when we specifically want to update it
  useEffect(() => {
    if (ref.current && shouldUpdateCursor && cursorPosition !== null && isUsePercentageFormat) {
      // Reset the flag first
      setShouldUpdateCursor(false);

      // Use multiple methods to ensure cursor position is set
      const setCursor = () => {
        if (ref.current) {
          ref.current.focus();
          ref.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      };

      // Try immediate
      setCursor();

      // Try with timeout as backup
      setTimeout(setCursor, 0);
    }
  }, [value, cursorPosition, shouldUpdateCursor, isUsePercentageFormat]);

  useEffect(() => {
    if (
      !isNaN(Number(props.value)) &&
      !isAlreadySetDefaultValue &&
      (isUsePercentageFormat || isUseCurrencyFormat)
    ) {
      if (isUsePercentageFormat) {
        setValue(formatPercentage(props.value.toString()));
      } else if (isUseCurrencyFormat) {
        setValue(formatCurrency(props.value.toString()));
      }
      setIsAlreadySetDefaultValue(true);
    }
  }, [props.value]);

  return (
    <div className={containerClassName}>
      <div className={`relative`}>
        {/* --- label --- */}
        {label && (
          <label className={labelClassName}>
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}

        {/* --- input --- */}
        <div
          className={`relative ${label ? 'mt-2' : ''} ${
            formatText ? 'flex items-center gap-2' : ''
          } flex justify-between ${inputWrapperClassName} `}
        >
          {/* -- icon --- */}
          {icon && (
            <div
              className={`flex items-center px-2 border-b ${isCampaignInquiry && '!border-[#5F5F5F]'} border-reliableBlack30 ${iconClassName} ${
                isInputFocus && 'border-reliableBlack60'
              } ${(danger || error) && 'border-supportiveRed'} ${props?.value ? 'cursor-pointer' : ''} ${
                iconPosition === 'right' ? 'order-1' : ''
              } ${disabled && '!bg-reliableBlack10 !text-reliableBlack50'}`}
              onClick={handleOnClickIcon}
            >
              {icon as React.ReactNode}
            </div>
          )}
          <input
            style={style}
            id={id}
            data-testid={dataTestId ?? id}
            placeholder={placeholder}
            type={type}
            maxLength={maxLength}
            disabled={disabled}
            className={`${dafaultStyling} ${sizeClassName[size]} ${inputClassName} ${
              danger && '!border-supportiveRed'
            } ${error && '!border-shadeRed'} ${focus && `focus:${focus}`}`}
            onFocus={(e) => (isUseCurrencyFormat ? handleFocus(e) : setIsInputFocus(true))}
            onBlur={() => setIsInputFocus(false)}
            onWheel={preventInputNumberOnWheel}
            onKeyDown={handleKeyDown}
            ref={inputRef || ref}
            {...register}
            {...props}
            onChange={(e) => {
              if (isUseCurrencyFormat) {
                handleChangeCurrency(e);
              } else if (isUsePercentageFormat) {
                handleChangePercentage(e);
              } else {
                if (props.onChange) {
                  props.onChange(e);
                }
              }
            }}
            value={isUseCurrencyFormat || isUsePercentageFormat ? value : props.value}
          />
          {prefixComponent}
          <p
            id="nav-result-search"
            className={formatText ? `flex-shrink-0 mt-2 w-[56px]` : `hidden`}
          >
            {formatText}
          </p>
        </div>
      </div>

      {/* --- showed when input already touched before and error has a string --- */}
      {!showMessageError ? (
        ''
      ) : error ? (
        <div className="flex items-center gap-[5.67px]">
          <span
            className={`text-supportiveRed text-[14px] leading-[20px] font-[400] ${errorClassName}`}
          >
            {error}
          </span>
        </div>
      ) : infoField ? (
        <span className="text-xs text-reliableBlack70">{infoField}</span>
      ) : (
        ''
      )}
    </div>
  );
};

export default Input;
